import React from 'react';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Button from '../../../components/ui/Button';

const QuoteDetailModal = ({ quote, onClose, onUpdateStatus }) => {
  const statusOptions = [
    { value: 'pending', label: 'En attente', color: 'orange' },
    { value: 'confirmed', label: 'Confirmé', color: 'green' },
    { value: 'expired', label: 'Expiré', color: 'gray' }
  ];

  const handleStatusChange = (newStatus) => {
    onUpdateStatus(quote?.id, newStatus);
    onClose();
  };

  const shippingType = quote?.quoteItems?.[0]?.shippingType || 'Non spécifié';
  const clientType = quote?.quoteItems?.[0]?.clientType || 'Non spécifié';
  const needsEX1 = quote?.quoteItems?.[0]?.needsEX1 || 'Non renseigné';
  const pickupAddress = quote?.quoteItems?.[0]?.pickupAddress || 'Non renseigné';
  const frequency = quote?.quoteItems?.[0]?.frequency || 'Non renseigné';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <Icon name="FileText" size={24} className="text-green-600" />
            <span>Détails du devis OpenAI</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Quote Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Référence</p>
                <p className="font-medium text-gray-900">#{quote?.id?.slice(0, 8)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Date de création</p>
                <p className="font-medium text-gray-900">
                  {format(new Date(quote?.createdAt), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Client</p>
                <p className="font-medium text-gray-900">{quote?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Email</p>
                <p className="font-medium text-gray-900">{quote?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Téléphone</p>
                <p className="font-medium text-gray-900">{quote?.phone || 'Non renseigné'}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Type de client</p>
                <p className="font-medium text-gray-900">{clientType}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Type d'envoi</p>
                <p className="font-medium text-gray-900">{shippingType}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Certificat EX1</p>
                <p className="font-medium text-gray-900">{needsEX1}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Adresse d'enlèvement</p>
                <p className="font-medium text-gray-900">{pickupAddress}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Fréquence</p>
                <p className="font-medium text-gray-900">{frequency}</p>
              </div>
            </div>
          </div>

          {/* Amount */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-600 font-medium mb-1">Montant estimé</p>
            <p className="text-3xl font-bold text-green-900">
              {parseFloat(quote?.totalAmount || 0)?.toFixed(2)} €
            </p>
          </div>

          {/* AI Generated Quote */}
          {quote?.quoteContent && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center space-x-2">
                <Icon name="Sparkles" size={20} className="text-green-600" />
                <span>Devis généré par OpenAI</span>
                <span className="ml-auto px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                  Score de confiance: 95%
                </span>
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                  {quote?.quoteContent}
                </pre>
              </div>
            </div>
          )}

          {/* Email Tracking */}
          <div className="space-y-3 mb-6">
            {quote?.confirmationEmailSentAt && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-green-800">
                  <Icon name="Mail" size={18} />
                  <span className="text-sm font-medium">
                    Email de confirmation envoyé le {format(new Date(quote?.confirmationEmailSentAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                  </span>
                </div>
              </div>
            )}

            {quote?.reminderScheduledFor && !quote?.reminderEmailSentAt && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-orange-800">
                  <Icon name="Clock" size={18} />
                  <span className="text-sm font-medium">
                    Rappel programmé pour le {format(new Date(quote?.reminderScheduledFor), 'dd MMM yyyy', { locale: fr })}
                  </span>
                </div>
              </div>
            )}

            {quote?.reminderEmailSentAt && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-800">
                  <Icon name="Send" size={18} />
                  <span className="text-sm font-medium">
                    Rappel envoyé le {format(new Date(quote?.reminderEmailSentAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Status Update */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Modifier le statut</h4>
            <div className="flex flex-wrap gap-2">
              {statusOptions?.map((option) => (
                <Button
                  key={option?.value}
                  onClick={() => handleStatusChange(option?.value)}
                  variant={quote?.quoteStatus === option?.value ? 'default' : 'outline'}
                  className="flex items-center space-x-2"
                >
                  <span>{option?.label}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetailModal;